import { DEFAULT_LOCALE, isAppLocale, type AppLocale } from '@/i18n/config';

export const FINANCE_YEARS = [2026, 2027, 2028] as const;

export type FinanceDemoYear = (typeof FINANCE_YEARS)[number];

const LOCALE_TAGS: Record<AppLocale, string> = {
  it: 'it-IT',
  en: 'en-US',
};

export function resolveFinanceLocale(value: string | undefined): AppLocale {
  return isAppLocale(value) ? value : DEFAULT_LOCALE;
}

/**
 * `FIN-FR-14`'s "no binary floating point" rule governs how figures are DERIVED: every total and
 * cumulative balance is summed in integer minor units (`buildCumulativeSeries` below, and the
 * server's own aggregation), never in euro decimals. This last division is the rendering step, and
 * it cannot change a displayed cent: for |valueMinor| below 2^53 the double nearest valueMinor/100
 * is within ~1e-16 relative of the exact value, so rounding to two fraction digits lands on the
 * same cent for any amount up to roughly 1e13 euros — four orders of magnitude above the largest
 * figure in this dataset (EUR 5.863.625,00).
 *
 * Feeding `Intl` an exact decimal STRING instead would need a cast to `Intl.StringNumericLiteral`,
 * since a computed `string` does not match that template-literal type — trading a proven-harmless
 * division for a type assertion, which this workspace treats as a non-fix.
 */
export function formatAmountMinor(valueMinor: number, locale: AppLocale): string {
  return new Intl.NumberFormat(LOCALE_TAGS[locale], {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(valueMinor / 100);
}

/**
 * Falls back to the raw value rather than throwing. `Intl.DateTimeFormat.format` throws
 * `RangeError: Invalid time value` on an unparseable date and the property access throws outright on
 * `null`/`undefined`, and this workspace mounts no error boundary — so one reshaped or nullable
 * `competenceMonth` in a server response replaced the whole Finance route with React Router's
 * default error screen instead of the module's own tested error state (SL-0053 review, console
 * finding 7). Element-level payload validation is the first line; this is the second.
 */
export function formatCompetenceMonth(value: string, locale: AppLocale): string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}/.test(value)) {
    return String(value ?? '');
  }

  const [yearPart, monthPart] = value.split('-');
  const year = Number(yearPart);
  const month = Number(monthPart);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return value;
  }

  const date = new Date(Date.UTC(year, month - 1, 1));
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(LOCALE_TAGS[locale], {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

/**
 * The group and decimal separators the ACTIVE locale actually uses, read from `Intl` rather than
 * assumed. `it-IT` groups with `.` and decimals with `,`; `en-US` is the exact inverse, which is why
 * a hardcoded Italian parse silently mis-scaled English input by 100x (see `parseAmountMinor`).
 */
function localeSeparators(locale: AppLocale): { group: string; decimal: string } {
  const parts = new Intl.NumberFormat(LOCALE_TAGS[locale]).formatToParts(1234.5);
  return {
    group: parts.find((part) => part.type === 'group')?.value ?? '.',
    decimal: parts.find((part) => part.type === 'decimal')?.value ?? ',',
  };
}

function escapeForRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Parses a user-typed amount into integer minor units, or returns `null` for anything it cannot read
 * UNAMBIGUOUSLY.
 *
 * WHY IT REFUSES RATHER THAN GUESSES (SL-0053 review, console finding 1). The previous
 * implementation ran `value.replace(/\./g, '').replace(',', '.')`, treating every dot as an Italian
 * thousands separator regardless of the active locale. With the console in English — a first-class,
 * browser-detected locale — typing `1234.56` produced `12345600`, i.e. EUR 123.456,00, and pasting
 * the app's own English-rendered `484,537.58` produced EUR 484,54. Nothing downstream could catch
 * it: a 100x-scaled amount is still a positive integer, so the datastore's `amount_minor > 0` check
 * passes and the figure lands durably in a regulated, append-only-audited table.
 *
 * The grammar is built from the active locale's own separators: either plain digits with an optional
 * decimal part, or properly grouped thousands with an optional decimal part. Input that satisfies
 * neither — including a decimal separator from the OTHER locale — is rejected so the caller can show
 * a field error, which is strictly better than accepting a number that means something else.
 *
 * Minor units are computed BEFORE the positivity check, so sub-cent input like `0,004` is rejected
 * as invalid at the field instead of passing a `parsed > 0` test and posting `amountMinor: 0` for the
 * datastore to refuse with a 500 (review finding 5).
 */
export function parseAmountMinor(value: string, locale: AppLocale): number | null {
  const { group, decimal } = localeSeparators(locale);
  // Users paste from spreadsheets, which group with NBSP and narrow-NBSP in several locales.
  const trimmed = value.trim().replace(/[\u00a0\u202f]/g, group);
  if (trimmed.length === 0) {
    return null;
  }

  const g = escapeForRegExp(group);
  const d = escapeForRegExp(decimal);
  const ungrouped = new RegExp(`^\\d+(?:${d}\\d{1,2})?$`);
  const grouped = new RegExp(`^\\d{1,3}(?:${g}\\d{3})+(?:${d}\\d{1,2})?$`);
  if (!ungrouped.test(trimmed) && !grouped.test(trimmed)) {
    return null;
  }

  const normalized = trimmed.split(group).join('').replace(decimal, '.');
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  const amountMinor = Math.round(parsed * 100);
  return amountMinor > 0 ? amountMinor : null;
}

/** Renders a stored amount back into the active locale's own decimal form for an edit field. */
export function formatAmountInput(amountMinor: number, locale: AppLocale): string {
  return new Intl.NumberFormat(LOCALE_TAGS[locale], {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  }).format(amountMinor / 100);
}

/**
 * Accepts `YYYY-MM` only for a month that exists and a year the module can actually display, and
 * returns the first-of-month date the datastore's own CHECK constraint requires.
 *
 * The previous `/^\d{4}-\d{2}$/` accepted `2026-00`, `2026-13`, `2026-99` and any year at all
 * (review finding 4). An impossible month reached Postgres and failed its date cast, surfacing as a
 * generic 5xx for a form the client had just declared valid; a valid-but-out-of-window year such as
 * `2029-05` succeeded end to end and then appeared on no screen, because the dashboard and register
 * only ever read `FINANCE_YEARS` — a movement silently absent from the product while sitting in the
 * ledger.
 */
export function parseCompetenceMonth(value: string): string | null {
  const match = /^(\d{4})-(\d{2})$/.exec(value.trim());
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) {
    return null;
  }
  if (!(FINANCE_YEARS as readonly number[]).includes(year)) {
    return null;
  }

  return `${match[1]}-${match[2]}-01`;
}

/**
 * Renders an import run's completion timestamp for a reader.
 *
 * Falls back to the raw string when the value does not parse, because the alternative is the literal
 * text "Invalid Date" on a screen a CEO reads. An unexpected format is a defect worth seeing, not
 * worth disguising.
 */
export function formatImportCompletedAt(isoTimestamp: string, locale: string): string {
  const parsed = new Date(isoTimestamp);
  if (Number.isNaN(parsed.getTime())) {
    return isoTimestamp;
  }

  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
    parsed,
  );
}
